import React, { useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "App/common/ErrorBoundary";
import styles from "./SpaceItems.module.scss";
import { useAuth } from "App/components/Auth";
import {
  getDefaultVersionId,
  hasRole,
  PERMISSION,
  spaceHasRolesCheck,
} from "@highnote/server/src/core/shared-util";
import { SORT_TYPE } from "../Library/config";
import { DEFAULT_COLUMNS, EntityTable } from "App/components/EntityTable";
import { useAttachmentsContext } from "App/common/useAttachments";
import {
  ENTITY_TYPE,
  EntityRowConfig,
  SpaceTrack,
} from "App/components/EntityTable/config";
import { highnote } from "@highnote/server/src/sdk";
import { useGlobalAudioPlayer } from "App/components/GlobalAudioPlayer";
import { UPLOAD_GROUP, useFiles } from "App/components/useFiles";
import { useSpaceContext } from "App/common/useSpace";
import { useGlobalSpaces } from "App/store/spaces/useGlobalSpaces";
import { useGlobalTracks } from "App/store/tracks/useGlobalTracks";
import { SPACE_TRACKS_WATCHER_COMPONENT_ID } from "App/store/watchers/useWatcherV2";
import { useSpaceChildItemRows } from "App/components/EntityTable/ExpandableSpace/ExpandableSpaceRow";
import { useLocation } from "react-router";
import { useIntercomContext } from "App/services/intercom/IntercomContext";
import { useDropboxJobsContext } from "App/components/useEntities/useDropboxDownloads";
import {
  COLLECTION_ID,
  Entity,
  JOB_STATUS,
  ROLE,
  SelectionType,
} from "@highnote/server/src/core/entities";
import { useEntitiesSelection } from "App/components/useEntitiesSelection";
import { getEntityCollectionFromType } from "App/common/BaseTable";
import { useDnDMonitor } from "App/components/DragAndDrop/DnDMonitor";
import { PinnedAdsList } from "App/common/Ads/PinnedAdsList";

// Re-exported for the tests and callers that import it from here; the
// definition lives next to the watcher so useSpace.tsx, SpaceTrack.tsx and
// this page all read the same id.
export { SPACE_TRACKS_WATCHER_COMPONENT_ID };

const DESKTOP_COLUMNS = [
  DEFAULT_COLUMNS.PREVIEW,
  DEFAULT_COLUMNS.CTA,
  DEFAULT_COLUMNS.ACTIONS,
];

const MOBILE_COLUMNS = [DEFAULT_COLUMNS.PREVIEW, DEFAULT_COLUMNS.CTA];

export const SpaceItems = ({
  sortType = SORT_TYPE.CUSTOM,
}: {
  sortType?: SORT_TYPE;
}) => {
  const { setShowLauncher } = useIntercomContext();
  const { dropboxJobs, resetPromptStatus } = useDropboxJobsContext();
  const { pathname } = useLocation();
  const { isAllowed, user } = useAuth();
  const { getUploadCache, getUploads } = useFiles();
  const { space, spaceLoading } = useSpaceContext();
  const {
    registerDnDContainer,
    unregisterDnDContainer,
    updateDnDSelectedEntities,
  } = useDnDMonitor();
  const { globalSpacesWatchers } = useGlobalSpaces();
  const { globalTracksWatchers } = useGlobalTracks();
  const spaceTracksWatcher = globalTracksWatchers.get(space?.id);
  const spaceTracksLoading = spaceTracksWatcher?.loading?.get(
    SPACE_TRACKS_WATCHER_COMPONENT_ID,
  );
  // A missing space-tracks-v1 join row is ALLOW + zero documents on the
  // wire — identical to a space with no tracks. Reading `error` here is
  // what keeps a rules regression or backfill gap from rendering as a
  // calm, silent empty space (see SPACE_TRACKS_WATCHER_COMPONENT_ID above).
  const childSpacesWatcher = globalSpacesWatchers.get(space?.id);
  // The child SPACES listener for this page fails the same way and
  // renders the same silent "empty" without this.
  const childItemsError =
    spaceTracksWatcher?.error || childSpacesWatcher?.error;
  const childSpacesLoading = childSpacesWatcher?.loading?.get(
    "space-home-child-item-rows",
  );

  const spaceHasUploadPermissions = spaceHasRolesCheck(space, [ROLE.UPLOAD]);
  const isUserOwner = space?.createdBy === user?.id;
  const userHasUploadPermissions =
    isUserOwner || hasRole(user?.id, ROLE.UPLOAD, space);
  const hideSelectionColumn =
    !spaceHasUploadPermissions && !userHasUploadPermissions;

  const [itemsOrder, setItemsOrder] = useState(space?.itemsOrder || []);
  const { getSortedSpaceChildItemRows } = useSpaceChildItemRows({
    space,
    options: {
      hasPlayableTracks: true,
      tableRowVariant: "expanded-row",
    },
  });

  const {
    selectedEntities,
    addToSelection,
    removeFromSelection,
    isSelectionVisible,
  } = useEntitiesSelection();
  useEffect(() => {
    // keep monitor in sync with current selected entities for group-drag
    updateDnDSelectedEntities(selectedEntities);
  }, [selectedEntities, updateDnDSelectedEntities]);

  const spaceChildItemRows = getSortedSpaceChildItemRows({
    sortType,
    itemsOrder,
  });

  const { isLoading: attachmentsLoading } = useAttachmentsContext();
  const { setQueue } = useGlobalAudioPlayer();

  const trackUploadCache = getUploadCache(
    UPLOAD_GROUP.TRACKS_BY_SPACE,
    space?.id,
  );
  const attachmentUploadCache = getUploadCache(
    UPLOAD_GROUP.ATTACHMENTS_BY_SPACE,
    space?.id,
  );
  const dropboxUpload = useMemo(() => {
    return dropboxJobs.map((job) => {
      return {
        file: {
          id: job.id,
          name: job.fileName,
          fileName: job.fileName,
        },
        progress: job.progress / 100,
        error: job.processingError,
        onRetry: () => {
          resetPromptStatus();
          highnote.retryDropboxJob({ jobId: job.id });
        },
        ...(job.status === JOB_STATUS.FAILED && {
          cancelUpload: () => {
            highnote.deleteDropboxJob({ jobId: job.id });
          },
        }),
      };
    });
  }, [dropboxJobs]);
  const trackUploads = getUploads({ cache: trackUploadCache });
  const attachmentUploads = getUploads({ cache: attachmentUploadCache });

  useEffect(() => {
    setShowLauncher(true);
  }, []);

  useEffect(() => {
    setItemsOrder(space?.itemsOrder || []);
  }, [space?.itemsOrder]);

  const canAddToSpace = isAllowed(PERMISSION.TO_ADD_TO_SPACE, { space });

  const uploadRows = useMemo(() => {
    const allUploads = [
      ...(dropboxUpload || []),
      ...(trackUploads || []),
      ...(attachmentUploads || []),
    ];
    return allUploads.map((uploadItem) => {
      return {
        id: uploadItem.file.id,
        entity: uploadItem,
        type: ENTITY_TYPE.UPLOAD,
      };
    });
  }, [dropboxUpload, trackUploads, attachmentUploads]);

  useEffect(() => {
    if (spaceTracksLoading) return;

    setQueue({
      type: "SET",
      _tracks: spaceChildItemRows
        .filter((item) => {
          if (item.type !== ENTITY_TYPE.TRACK) return false;
          const track = item.entity as SpaceTrack;
          // A row-backed tile (space-tracks-v1) has NO `versionFilesV2` — the
          // join row carries a render header, not the version array. Gating
          // queue membership on that array alone silently dropped every
          // borrowed track from the queue, so the space page's Play was
          // disabled and no file fetch ever happened; the tile just sat on
          // its placeholder, which looks like "still processing".
          //
          // `getDefaultVersionId` resolves a row-only tile via the
          // `defaultVersionId` that buildSpaceTrackTile projects from the
          // row, so this admits borrowed tracks while still excluding a
          // track with genuinely nothing playable.
          return (
            (track.versionFilesV2 || []).length > 0 ||
            Boolean(getDefaultVersionId(track))
          );
        })
        .map((item) => item.entity as SpaceTrack),
      currentPageId: pathname,
    });
  }, [spaceChildItemRows, spaceTracksLoading, pathname]);

  const onMoveEnd = (newList: EntityRowConfig[]) => {
    const newOrder = newList.map((item) => item.id);
    setItemsOrder(newOrder);
    highnote.updateSpace({
      id: space?.id,
      data: {
        itemsOrder: newOrder,
      },
    });
  };

  const onSelectRow = (row: EntityRowConfig, parentRow?: EntityRowConfig) => {
    const entityId = (row.entity as Entity).id;

    const isAlreadySelected = selectedEntities[entityId];

    if (isAlreadySelected) {
      removeFromSelection(entityId);
      return;
    }

    const entityType = getEntityCollectionFromType(row.type);
    // The space this row is currently displayed under — the top-level space
    // page, or the nested child space row it's expanded within. Used below
    // as the VIEWING space on TRACK selections, never track.spaceId (that
    // field is the track's ORIGIN and must stay independent so the bulk
    // hard-delete guard (deleteOrRemoveEntities) can tell a real delete
    // apart from a delete issued while merely borrowing the track).
    const viewingSpaceId = parentRow
      ? (parentRow.entity as Entity).id
      : space.id;

    addToSelection({
      entityId,
      entityType,
      parentEntity: {
        entityId: viewingSpaceId,
        entityType: parentRow
          ? getEntityCollectionFromType(parentRow.type)
          : COLLECTION_ID.SPACE,
      },
      ...(entityType === COLLECTION_ID.TRACK && { spaceId: viewingSpaceId }),
    } as SelectionType);

    return;
  };

  const isDraggable = canAddToSpace && sortType === SORT_TYPE.CUSTOM;

  const allRows = useMemo(() => {
    return [...spaceChildItemRows, ...uploadRows].map((row) => {
      return {
        ...row,
        // Provide container context for DnD V2 monitor
        containerId: space?.id || null,
        selected: row.id in selectedEntities,
      };
    });
  }, [spaceChildItemRows, uploadRows, selectedEntities, space?.id]);
  const isFetching =
    spaceLoading ||
    spaceTracksLoading ||
    childSpacesLoading ||
    attachmentsLoading;
  const hasRows = allRows.length > 0;
  const isLoading = !hasRows && isFetching;

  useEffect(() => {
    if (!space?.id) return;
    registerDnDContainer(space.id, {
      getItemsOrder: () => allRows.map((row) => row.id),
      applyReorder: (newOrder: string[]) => {
        // Optimistic update mirrors onMoveEnd
        setItemsOrder(newOrder);
        highnote.updateSpace({
          id: space.id,
          data: { itemsOrder: newOrder },
        });
      },
      containerEntityType: ENTITY_TYPE.SPACE,
    });
    return () => {
      unregisterDnDContainer(space.id);
    };
  }, [space?.id, allRows, registerDnDContainer, unregisterDnDContainer]);

  // Render an error state that a reader can tell apart from both "still
  // loading" and "genuinely empty" at a glance — an ALLOW + zero-documents
  // response looks identical to an empty space on the wire, so this is the
  // only place that gap becomes visible to the user instead of silently
  // reading as "this space has no tracks".
  if (childItemsError) {
    return (
      <ErrorBoundary name="SpaceItems">
        <PinnedAdsList />
        <div className={styles["error-state"]}>
          We couldn&apos;t load the tracks in this space. Please try again or{" "}
          <a href="mailto:help@highnote.fm" rel="noreferrer" target="_blank">
            contact&nbsp;support.
          </a>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary name="SpaceItems">
      {/*
        Pinned ads render ABOVE the entity table, not as table rows.
        Keeping them outside the table avoids forcing ads through the
        EntityTable row schema (no drag handle, no selection, no
        context menu) and renders nothing when the feature is
        disabled or when there are no pinned ads — zero visual
        footprint in those cases.
      */}
      <PinnedAdsList />
      <EntityTable
        tableRowVariant="draggable-expanded-row"
        hideSelectionColumn={hideSelectionColumn}
        isLoading={isLoading}
        rows={allRows}
        onSelect={onSelectRow}
        isSelectionEnabled={isSelectionVisible}
        columns={DESKTOP_COLUMNS}
        mobileColumns={MOBILE_COLUMNS}
        onDragEnd={isDraggable ? onMoveEnd : undefined}
        enableDrag={isDraggable}
        EmptyComponent={() => (
          <div className="highnote-empty-table">
            <p>
              <strong>It&apos;s empty in here.</strong>
              {canAddToSpace && "Create or add content to begin."}
            </p>
          </div>
        )}
      />
    </ErrorBoundary>
  );
};
